import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "./Portal.css";
import PollCard from "../components/PollCard";

interface PollOption {
  optionId: string;
  optionText: string;
  emoji?: string;
  voteCount: number;
}

interface Poll {
  pollId: string;
  title: string;
  category: string;
  primaryColor?: string;
  secondaryColor?: string;
  imageUrl?: string;
  createdByUserId?: string;
  createdAt: string;
  options: PollOption[];
}

export default function Portal() {
  const [polls, setPolls] = useState<Poll[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const apiBase = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

  const fetchPolls = useCallback(async () => {
    setLoading(true);
    setGlobalError(null);
    try {
      const res = await axios.get(`${apiBase}/api/polls/portal`, {
        params: { page, pageSize: 10, category },
      });
      setPolls(res.data.polls);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("Failed to fetch polls", err);
      setGlobalError("Failed to load polls. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [page, category, apiBase]);

  useEffect(() => {
    fetchPolls();
  }, [fetchPolls]);

  const handleVote = async (pollId: string, optionId: string) => {
    try {
      const res = await axios.post(
        `${apiBase}/api/polls/${pollId}/vote`,
        { optionId },
        { headers: { "Content-Type": "application/json" } }
      );
      const updatedPoll: Poll = res.data;
      setPolls(prev => prev.map(p => (p.pollId === pollId ? updatedPoll : p)));
    } catch (err) {
      console.error("Vote failed", err);
      alert("Vote failed. Please try again.");
    }
  };

  return (
    <div className="polls-container">
      <h2>Polls</h2>

      <div className="filter-bar">
        <label>Filter by category:</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All</option>
          <option value="Sports">Sports</option>
          <option value="Tech">Tech</option>
          <option value="Politics">Politics</option>
          <option value="Entertainment">Entertainment</option>
          <option value="Food">Food</option>
          <option value="Travel">Travel</option>
        </select>
      </div>

      {loading && <div className="loading">Loading polls...</div>}
      {globalError && <div className="error-msg">{globalError}</div>}

      {!loading && !globalError && (
        <>
          <div className="polls-grid">
            {polls.map((poll) => (
              <PollCard
                key={poll.pollId}
                poll={poll}
                onVote={handleVote}
                editable={false} // Portal only shows voting
              />
            ))}
          </div>

          <div className="pagination">
            <button
              disabled={page === 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
